<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
    </div>

    <!-- No lock yet — generate -->
    <div v-else-if="!lock" class="space-y-6 py-6">
      <div class="text-center">
        <h3 class="text-lg font-semibold font-heading text-gray-900 dark:text-gray-100 mb-2">
          Heading &amp; Meta Structure
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Confirm the primary keyword and verified inputs before generating title, meta, H1, and heading options.
        </p>
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Confirmed Keyword</label>
          <input
            v-model="confirmedKeyword"
            type="text"
            class="mt-2 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
        </div>
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Input Readiness</p>
          <p class="mt-2 text-sm text-gray-900 dark:text-gray-100">
            {{ readinessSummary }}
          </p>
        </div>
      </div>

      <div v-if="readinessWarnings.length" class="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 space-y-1">
        <p v-for="warning in readinessWarnings" :key="warning" class="text-sm text-amber-800 dark:text-amber-300">
          {{ warning }}
        </p>
      </div>
      <div v-if="readinessBlocking.length" class="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 space-y-1">
        <p v-for="message in readinessBlocking" :key="message" class="text-sm text-red-700 dark:text-red-300">
          {{ message }}
        </p>
      </div>

      <div class="flex justify-end">
        <button @click="handleGenerate" :disabled="generating || !canGenerate" class="btn btn-primary disabled:opacity-50">
          {{ generating ? 'Generating...' : 'Generate Options' }}
        </button>
      </div>
    </div>

    <!-- Generating -->
    <div v-else-if="lock.status === 'pending' || lock.status === 'generating'" class="text-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
      <p class="text-sm text-gray-600 dark:text-gray-400">
        {{ progressMessage || 'Generating heading & meta options...' }}
      </p>
    </div>

    <!-- Failed -->
    <div v-else-if="lock.status === 'failed'" class="text-center py-12">
      <p class="text-sm text-red-600 dark:text-red-400 mb-4">
        {{ lock.error_message || 'Generation failed. Please try again.' }}
      </p>
      <button @click="handleGenerate" class="btn btn-primary">
        Retry
      </button>
    </div>

    <!-- Ready / Completed — wizard steps -->
    <div v-else class="space-y-6">
      <!-- Step Progress Bar -->
      <div class="flex items-center justify-between px-4">
        <div v-for="(step, i) in steps" :key="step.key" class="flex items-center" :class="{ 'flex-1': i < steps.length - 1 }">
          <div class="flex flex-col items-center">
            <div
              :class="[
                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors',
                stepDotClass(step.key)
              ]">
              <span v-if="stepStatus(step.key) === 'locked'" class="text-white">&#10003;</span>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <span class="text-xs mt-1 whitespace-nowrap" :class="stepLabelClass(step.key)">
              {{ step.label }}
            </span>
          </div>
          <div v-if="i < steps.length - 1" class="flex-1 h-0.5 mx-2 mt-[-12px]" :class="stepLineClass(step.key)" />
        </div>
      </div>

      <!-- Error -->
      <div v-if="error" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
        <p class="text-sm text-red-700 dark:text-red-400">{{ error }}</p>
      </div>

      <!-- Existing page structure -->
      <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4">
        <div class="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Existing Page Structure
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Current extracted title, meta, H1, and heading outline for this page.
            </p>
          </div>
          <span v-if="existingStructure.last_extract_content_at" class="text-xs text-gray-500 dark:text-gray-400 shrink-0">
            Extracted {{ formatDate(existingStructure.last_extract_content_at) }}
          </span>
        </div>

        <div class="grid gap-4 md:grid-cols-3">
          <div>
            <div class="flex items-center justify-between gap-2 mb-1">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Title Tag
              </p>
              <span v-if="existingTitleTag" :class="charCountClass(existingTitleTag, 60)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
                {{ existingTitleTag.length }} chars
              </span>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {{ sourceLabel(existingStructure.title_source) }}
            </p>
            <p v-if="existingTitleTag" class="text-sm text-gray-900 dark:text-gray-100">
              {{ existingTitleTag }}
            </p>
            <p v-else class="text-sm italic text-gray-400 dark:text-gray-500">
              Not found in extracted source
            </p>
          </div>

          <div>
            <div class="flex items-center justify-between gap-2 mb-1">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Meta Description
              </p>
              <span v-if="existingMetaDescription" :class="charCountClass(existingMetaDescription, 155)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
                {{ existingMetaDescription.length }} chars
              </span>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {{ sourceLabel(existingStructure.meta_description_source) }}
            </p>
            <p v-if="existingMetaDescription" class="text-sm text-gray-900 dark:text-gray-100">
              {{ existingMetaDescription }}
            </p>
            <p v-else class="text-sm italic text-gray-400 dark:text-gray-500">
              Not found in extracted source
            </p>
          </div>

          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              H1
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {{ sourceLabel(existingStructure.h1_source) }}
            </p>
            <p v-if="existingH1" class="text-sm text-gray-900 dark:text-gray-100">
              {{ existingH1 }}
            </p>
            <p v-else class="text-sm italic text-gray-400 dark:text-gray-500">
              Not found in extracted source
            </p>
          </div>
        </div>

        <div class="mt-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Heading Structure
          </p>
          <div v-if="existingHeadings.length" class="max-h-64 overflow-y-auto rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 p-3 space-y-1">
            <div
              v-for="(heading, i) in existingHeadings"
              :key="'existing-h-' + i"
              class="flex items-center gap-2"
              :style="{ paddingLeft: `${Math.max(0, (heading.level || 1) - 1) * 18}px` }">
              <span :class="headingLevelClass(heading.level || 1)" class="text-xs font-bold px-2 py-0.5 rounded-lg shrink-0">
                H{{ heading.level || '?' }}
              </span>
              <span class="text-sm text-gray-900 dark:text-gray-100">
                {{ heading.text || '(no text)' }}
              </span>
            </div>
          </div>
          <p v-else class="text-sm italic text-gray-400 dark:text-gray-500">
            No headings extracted
          </p>
        </div>
      </div>

      <!-- Locked steps summaries -->
      <div v-for="step in lockedSteps" :key="'locked-' + step" class="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/10 p-4">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <span class="text-xs font-medium text-primary-700 dark:text-primary-400 uppercase tracking-wide">
              {{ stepLabel(step) }} — Locked
            </span>
            <p class="text-sm text-gray-900 dark:text-gray-100 mt-1">{{ lockedValue(step) }}</p>
          </div>
          <button @click="handleUnlock(step)" class="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1">
            Unlock
          </button>
        </div>
      </div>

      <!-- Step 1: Title Tag -->
      <div v-if="lock.current_step === 'title_tag'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 1: Choose Title Tag
        </h3>

        <!-- Current page title -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Existing Title Tag
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {{ sourceLabel(existingStructure.title_source) }}
          </p>
          <div v-if="existingTitleTag" class="flex items-start justify-between gap-3">
            <p class="text-sm text-gray-900 dark:text-gray-100 flex-1">
              {{ existingTitleTag }}
            </p>
            <span :class="charCountClass(existingTitleTag, 60)" class="text-xs px-2 py-0.5 rounded-lg font-medium shrink-0">
              {{ existingTitleTag.length }} chars
            </span>
          </div>
          <p v-else class="text-sm italic text-gray-400 dark:text-gray-500">
            Not found in extracted source
          </p>
        </div>

        <!-- Competitor patterns -->
        <div v-if="lock.competitor_title_patterns && lock.competitor_title_patterns.length" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Competitor Title Patterns</p>
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead>
                <tr class="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th class="py-2 pr-3">Rank</th>
                  <th class="py-2 pr-3">Competitor</th>
                  <th class="py-2 pr-3">Title Tag</th>
                  <th class="py-2 pr-3">Length</th>
                  <th class="py-2 pr-3">Keyword</th>
                  <th class="py-2 pr-3">Type</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(title, i) in lock.competitor_title_patterns" :key="i" class="border-b border-gray-100 dark:border-gray-800">
                  <td class="py-2 pr-3 text-gray-600 dark:text-gray-400">{{ title.position || '---' }}</td>
                  <td class="py-2 pr-3 text-gray-900 dark:text-gray-100">{{ title.domain }}</td>
                  <td class="py-2 pr-3 text-gray-700 dark:text-gray-300">{{ title.title || 'Not found' }}</td>
                  <td class="py-2 pr-3 text-gray-600 dark:text-gray-400">{{ title.length || 0 }}</td>
                  <td class="py-2 pr-3 text-gray-600 dark:text-gray-400">{{ title.keyword_usage || 'unknown' }}</td>
                  <td class="py-2 pr-3 text-gray-600 dark:text-gray-400">{{ title.page_type || 'Unknown' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- AI returned no options -->
        <div v-if="!(lock.title_tag_options || []).length" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">
            AI didn't return title suggestions. Click Regenerate to try again, or enter a custom title below.
          </p>
        </div>

        <!-- AI Options -->
        <div class="space-y-3">
          <div
            v-for="(option, i) in (lock.title_tag_options || [])"
            :key="i"
            @click="selectedTitle = i"
            :class="[
              'rounded-xl border-2 p-4 cursor-pointer transition-colors',
              selectedTitle === i
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            ]">
            <div class="flex items-start justify-between">
              <p class="text-sm text-gray-900 dark:text-gray-100 font-medium flex-1">{{ option.text }}</p>
              <div class="flex items-center gap-2 ml-3 shrink-0">
                <span :class="charCountClass(option.text, 60)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
                  {{ (option.text || '').length }} chars
                </span>
              </div>
            </div>
            <div class="flex items-center gap-2 mt-2">
              <span v-if="option.keyword_position" class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-0.5">
                Keyword position: {{ option.keyword_position }}
              </span>
              <span v-if="option.source" class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-0.5">
                {{ option.source }}
              </span>
            </div>
            <p v-if="option.quality_validation?.warnings?.length" class="mt-2 text-xs text-amber-700 dark:text-amber-400">
              {{ option.quality_validation.warnings[0] }}
            </p>
          </div>
        </div>

        <!-- Custom input -->
        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Or enter custom title:</label>
          <input
            v-model="customTitle"
            @focus="selectedTitle = null"
            type="text"
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter custom title tag...">
          <div class="flex justify-end">
            <span :class="charCountClass(customTitle, 60)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
              {{ customTitle.length }} / 60
            </span>
          </div>
        </div>

        <!-- Regenerate widget -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2">
          <div class="flex items-center gap-2">
            <input
              v-model="titleGuidance"
              type="text"
              placeholder="Optional: guide the AI (e.g., 'shorter', 'include free quote')"
              class="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
            <button
              @click="handleRegenerateTitle"
              :disabled="regenerating.title || locking"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
              <svg v-if="regenerating.title" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              {{ regenerating.title ? 'Regenerating...' : 'Regenerate' }}
            </button>
          </div>
          <p v-if="regenerateError.title" class="text-xs text-red-600 dark:text-red-400">
            {{ regenerateError.title }}
          </p>
        </div>

        <div class="flex justify-end">
          <button @click="handleLockTitle" :disabled="locking || regenerating.title" class="btn btn-primary">
            {{ locking ? 'Locking...' : 'Lock Title Tag' }}
          </button>
        </div>
      </div>

      <!-- Step 2: Meta Description -->
      <div v-if="lock.current_step === 'meta_description'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 2: Choose Meta Description
        </h3>

        <!-- Current page meta description -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Existing Meta Description
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {{ sourceLabel(existingStructure.meta_description_source) }}
          </p>
          <div v-if="existingMetaDescription" class="flex items-start justify-between gap-3">
            <p class="text-sm text-gray-900 dark:text-gray-100 flex-1">
              {{ existingMetaDescription }}
            </p>
            <span :class="charCountClass(existingMetaDescription, 155)" class="text-xs px-2 py-0.5 rounded-lg font-medium shrink-0">
              {{ existingMetaDescription.length }} chars
            </span>
          </div>
          <p v-else class="text-sm italic text-gray-400 dark:text-gray-500">
            Not extracted
          </p>
        </div>

        <!-- AI returned no options -->
        <div v-if="!(lock.meta_description_options || []).length" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">
            AI didn't return meta description suggestions. Click Regenerate to try again, or enter a custom description below.
          </p>
        </div>

        <div class="space-y-3">
          <div
            v-for="(option, i) in (lock.meta_description_options || [])"
            :key="i"
            @click="selectedMeta = i"
            :class="[
              'rounded-xl border-2 p-4 cursor-pointer transition-colors',
              selectedMeta === i
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            ]">
            <p class="text-sm text-gray-900 dark:text-gray-100">{{ option.text }}</p>
            <div class="flex items-center gap-2 mt-2">
              <span :class="charCountClass(option.text, 155)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
                {{ (option.text || '').length }} chars
              </span>
              <span v-if="option.has_keyword" class="text-xs text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 rounded-lg px-2 py-0.5">
                Has keyword
              </span>
              <span v-if="option.has_cta" class="text-xs text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 rounded-lg px-2 py-0.5">
                Has CTA
              </span>
            </div>
            <p v-if="option.quality_validation?.warnings?.length" class="mt-2 text-xs text-amber-700 dark:text-amber-400">
              {{ option.quality_validation.warnings[0] }}
            </p>
          </div>
        </div>

        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Or enter custom description:</label>
          <textarea
            v-model="customMeta"
            @focus="selectedMeta = null"
            rows="3"
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter custom meta description..." />
          <div class="flex justify-end">
            <span :class="charCountClass(customMeta, 155)" class="text-xs px-2 py-0.5 rounded-lg font-medium">
              {{ customMeta.length }} / 155
            </span>
          </div>
        </div>

        <label class="flex items-start gap-2 rounded-xl border border-gray-200 dark:border-gray-700 p-3 text-sm text-gray-700 dark:text-gray-300">
          <input v-model="metaOverrideWarnings" type="checkbox" class="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500">
          <span>I reviewed this meta description and want to continue even if only a soft CTA warning remains.</span>
        </label>

        <!-- Regenerate widget -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2">
          <div class="flex items-center gap-2">
            <input
              v-model="metaGuidance"
              type="text"
              placeholder="Optional: guide the AI (e.g., 'add urgency', 'mention free quote')"
              class="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
            <button
              @click="handleRegenerateMeta"
              :disabled="regenerating.meta || locking"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
              <svg v-if="regenerating.meta" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              {{ regenerating.meta ? 'Regenerating...' : 'Regenerate' }}
            </button>
          </div>
          <p v-if="regenerateError.meta" class="text-xs text-red-600 dark:text-red-400">
            {{ regenerateError.meta }}
          </p>
        </div>

        <div class="flex justify-end">
          <button @click="handleLockMeta" :disabled="locking || regenerating.meta" class="btn btn-primary">
            {{ locking ? 'Locking...' : 'Lock Meta Description' }}
          </button>
        </div>
      </div>

      <!-- Step 3: H1 -->
      <div v-if="lock.current_step === 'h1'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 3: Choose H1
        </h3>

        <!-- Current page H1 -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Existing H1
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {{ sourceLabel(existingStructure.h1_source) }}
          </p>
          <p v-if="existingH1" class="text-sm text-gray-900 dark:text-gray-100">
            {{ existingH1 }}
          </p>
          <p v-else class="text-sm italic text-gray-400 dark:text-gray-500">
            Not extracted
          </p>
        </div>

        <!-- AI returned no options -->
        <div v-if="!(lock.h1_options || []).length" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">
            AI didn't return H1 suggestions. Click Regenerate to try again, or enter a custom H1 below.
          </p>
        </div>

        <div class="space-y-3">
          <div
            v-for="(option, i) in (lock.h1_options || [])"
            :key="i"
            @click="selectedH1 = i"
            :class="[
              'rounded-xl border-2 p-4 cursor-pointer transition-colors',
              selectedH1 === i
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            ]">
            <p class="text-sm text-gray-900 dark:text-gray-100 font-medium">{{ option.text }}</p>
            <div class="flex items-center gap-2 mt-2">
              <span v-if="option.formula" class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-0.5">
                {{ option.formula }}
              </span>
              <span v-if="option.source" class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-0.5">
                {{ option.source }}
              </span>
            </div>
          </div>
        </div>

        <div class="space-y-1">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Or enter custom H1:</label>
          <input
            v-model="customH1"
            @focus="selectedH1 = null"
            type="text"
            class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter custom H1...">
        </div>

        <!-- Keyword warning for custom H1 -->
        <div v-if="customH1 && !customH1ContainsKeyword" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">
            Warning: Your custom H1 does not appear to contain the target keyword.
          </p>
        </div>

        <!-- Regenerate widget -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2">
          <div class="flex items-center gap-2">
            <input
              v-model="h1Guidance"
              type="text"
              placeholder="Optional: guide the AI (e.g., 'how-to format', 'include location')"
              class="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
            <button
              @click="handleRegenerateH1"
              :disabled="regenerating.h1 || locking"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
              <svg v-if="regenerating.h1" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              {{ regenerating.h1 ? 'Regenerating...' : 'Regenerate' }}
            </button>
          </div>
          <p v-if="regenerateError.h1" class="text-xs text-red-600 dark:text-red-400">
            {{ regenerateError.h1 }}
          </p>
        </div>

        <div class="flex justify-end">
          <button @click="handleLockH1" :disabled="locking || regenerating.h1" class="btn btn-primary">
            {{ locking ? 'Locking...' : 'Lock H1' }}
          </button>
        </div>
      </div>

      <!-- Step 4: Heading Structure -->
      <div v-if="lock.current_step === 'heading_structure'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 4: Heading Structure
        </h3>

        <!-- Current page heading structure -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-4">
          <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Current Page Heading Structure
          </p>
          <div v-if="existingHeadings.length" class="space-y-1">
            <div
              v-for="(heading, i) in existingHeadings"
              :key="'cur-h-' + i"
              class="flex items-center gap-2"
              :style="{ paddingLeft: `${Math.max(0, (heading.level || 1) - 1) * 24}px` }">
              <span :class="headingLevelClass(heading.level || 1)" class="text-xs font-bold px-2 py-0.5 rounded-lg shrink-0">
                H{{ heading.level || '?' }}
              </span>
              <span class="text-sm text-gray-900 dark:text-gray-100">
                {{ heading.text || '(no text)' }}
              </span>
            </div>
          </div>
          <p v-else class="text-sm italic text-gray-400 dark:text-gray-500">
            No headings extracted
          </p>
        </div>

        <!-- AI returned no options -->
        <div v-if="!(lock.heading_structure_recommendation || []).length" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">
            AI didn't return heading suggestions. Click Regenerate to try again, or build the structure manually using the Add Heading button below.
          </p>
        </div>

        <!-- Heading tree editor -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-2">
          <div
            v-for="(heading, i) in editingHeadings"
            :key="i"
            class="flex items-center gap-2 rounded-lg"
            :class="{
              'opacity-50': draggingIndex === i,
              'ring-2 ring-primary-400': dragOverIndex === i && draggingIndex !== null && draggingIndex !== i
            }"
            :style="{ paddingLeft: `${(heading.level - 2) * 24}px` }"
            :draggable="dragEnabledIndex === i"
            @dragstart="onDragStart(i, $event)"
            @dragover.prevent="dragOverIndex = i"
            @drop.prevent="onDrop(i)"
            @dragend="onDragEnd">
            <!-- Drag handle -->
            <span
              class="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 dark:hover:text-gray-400 shrink-0"
              title="Drag to reorder"
              @mousedown="dragEnabledIndex = i"
              @mouseup="dragEnabledIndex = null">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M7 4a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm6-15a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2zm0 5a1 1 0 110-2 1 1 0 010 2z" /></svg>
            </span>

            <!-- Level badge -->
            <span :class="headingLevelClass(heading.level)" class="text-xs font-bold px-2 py-0.5 rounded-lg shrink-0">
              H{{ heading.level }}
            </span>

            <!-- Editable text -->
            <input
              v-model="heading.text"
              type="text"
              class="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              :placeholder="`H${heading.level} heading text...`">

            <!-- Keywords pills -->
            <div v-if="heading.keywords && heading.keywords.length" class="flex items-center gap-1 shrink-0">
              <span
                v-for="(kw, ki) in heading.keywords"
                :key="ki"
                class="text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg px-1.5 py-0.5">
                {{ kw }}
              </span>
            </div>

            <!-- Data source tag -->
            <span v-if="heading.source" class="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded-lg px-1.5 py-0.5 shrink-0">
              {{ formatSource(heading.source) }}
            </span>

            <!-- Controls -->
            <div class="flex items-center gap-1 shrink-0">
              <button
                :disabled="i === 0"
                @click="moveHeading(i, -1)"
                class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move up">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" /></svg>
              </button>
              <button
                :disabled="i === editingHeadings.length - 1"
                @click="moveHeading(i, 1)"
                class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Move down">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
              <button
                v-if="heading.level > 2"
                @click="outdentHeading(i)"
                class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Outdent">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7" /></svg>
              </button>
              <button
                v-if="heading.level < 4"
                @click="indentHeading(i)"
                class="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Indent">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7" /></svg>
              </button>
              <button
                @click="removeHeading(i)"
                class="p-1 text-gray-400 hover:text-red-500"
                title="Remove">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          <!-- Add heading button -->
          <button @click="addHeading" class="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-1 mt-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
            Add Heading
          </button>
        </div>

        <!-- Nesting validation -->
        <div v-if="nestingWarning" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">{{ nestingWarning }}</p>
        </div>

        <!-- Regenerate widget -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3 space-y-2">
          <div class="flex items-center gap-2">
            <input
              v-model="headingStructureGuidance"
              type="text"
              placeholder="Optional: guide the AI (e.g., 'add an FAQ section', 'fewer H3s')"
              class="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
            <button
              @click="handleRegenerateHeadingStructure"
              :disabled="regenerating.structure || locking"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
              <svg v-if="regenerating.structure" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              {{ regenerating.structure ? 'Regenerating...' : 'Regenerate' }}
            </button>
          </div>
          <p class="text-xs text-amber-700 dark:text-amber-400">
            Heads up: regenerating replaces your current heading list with a new AI suggestion. Inline edits will be lost.
          </p>
          <p v-if="regenerateError.structure" class="text-xs text-red-600 dark:text-red-400">
            {{ regenerateError.structure }}
          </p>
        </div>

        <div class="flex justify-end">
          <button @click="handleLockHeadingStructure" :disabled="locking || regenerating.structure" class="btn btn-primary">
            {{ locking ? 'Locking...' : 'Lock Heading Structure' }}
          </button>
        </div>
      </div>

      <!-- Step 5: Keyword Verification -->
      <div v-if="lock.current_step === 'keyword_verification'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 5: Keyword Verification
        </h3>

        <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50">
            <span class="font-medium text-gray-900 dark:text-gray-100">Keyword Coverage</span>
            <span class="text-sm text-gray-500 dark:text-gray-400">
              {{ lock.verification_summary?.covered || 0 }} of {{ lock.verification_summary?.total || 0 }} keywords covered
            </span>
          </div>
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th class="px-4 py-2">Keyword</th>
                <th class="px-4 py-2">Priority</th>
                <th class="px-4 py-2">Status</th>
                <th class="px-4 py-2">In Headings</th>
                <th class="px-4 py-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(kv, i) in (lock.heading_keyword_verification || [])"
                :key="i"
                :class="kv.present ? 'bg-primary-50 dark:bg-primary-900/10' : 'bg-red-50 dark:bg-red-900/10'"
                class="border-t border-gray-100 dark:border-gray-800">
                <td class="px-4 py-2 text-gray-900 dark:text-gray-100">{{ kv.keyword }}</td>
                <td class="px-4 py-2">
                  <span :class="priorityClass(kv.priority)" class="text-xs font-medium px-2 py-0.5 rounded-lg">
                    {{ kv.priority || 'Optional' }}
                  </span>
                </td>
                <td class="px-4 py-2">
                  <span v-if="kv.present" class="text-green-600 dark:text-green-400 text-xs font-medium">Present</span>
                  <span v-else class="text-red-600 dark:text-red-400 text-xs font-medium">Gap</span>
                </td>
                <td class="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                  {{ (kv.found_in || []).join(', ') || (kv.ignored_in || []).join(', ') || '---' }}
                </td>
                <td class="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                  {{ kv.reason || 'No reason available.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex justify-end gap-3">
          <button @click="handleUnlock('heading_structure')" class="btn btn-secondary btn-sm">
            Go Back to Fix
          </button>
          <button @click="handleAcceptVerification" :disabled="locking" class="btn btn-primary">
            {{ locking ? 'Continuing...' : 'Accept & Continue' }}
          </button>
        </div>
      </div>

      <!-- Step 6: Target Lock -->
      <div v-if="lock.current_step === 'target_lock'" class="space-y-4">
        <h3 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100">
          Step 6: Select & Confirm Targets
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Select which keywords to target in the content. Uncheck any that aren't relevant to this page.
        </p>

        <div v-if="targetBlockingMessages.length" class="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 space-y-1">
          <p v-for="message in targetBlockingMessages" :key="message" class="text-sm text-red-700 dark:text-red-400">
            {{ message }}
          </p>
        </div>

        <div v-if="targetWarnings.length" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 space-y-1">
          <p v-for="warning in targetWarnings" :key="warning" class="text-sm text-amber-700 dark:text-amber-400">
            {{ warning }}
          </p>
        </div>

        <!-- Selection summary -->
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-600 dark:text-gray-400">
            {{ selectedKeywords.size }} of {{ (lock.locked_targets || []).length }} keywords selected
          </span>
          <div class="flex gap-2">
            <button @click="selectAllKeywords" class="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline">
              Select All
            </button>
            <span class="text-gray-300 dark:text-gray-600">|</span>
            <button @click="deselectAllKeywords" class="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline">
              Deselect All
            </button>
          </div>
        </div>

        <!-- Target table -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                <th class="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    :checked="allKeywordsSelected"
                    :indeterminate="someKeywordsSelected && !allKeywordsSelected"
                    @change="toggleAllKeywords"
                    class="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500" />
                </th>
                <th class="px-4 py-2">Keyword</th>
                <th class="px-4 py-2">Volume</th>
                <th class="px-4 py-2">Tier</th>
                <th class="px-4 py-2">Priority</th>
                <th v-for="comp in targetCompetitorColumns" :key="comp" class="px-4 py-2">{{ comp }}</th>
                <th class="px-4 py-2">Raw Avg</th>
                <th class="px-4 py-2">Cleaned Avg</th>
                <th class="px-4 py-2">Target</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(target, i) in sortedTargets"
                :key="i"
                :class="[
                  'border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50',
                  !selectedKeywords.has(target.keyword) && 'opacity-40'
                ]"
                @click="toggleKeyword(target.keyword)">
                <td class="px-3 py-2">
                  <input
                    type="checkbox"
                    :checked="selectedKeywords.has(target.keyword)"
                    @click.stop="toggleKeyword(target.keyword)"
                    class="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500" />
                </td>
                <td class="px-4 py-2 text-gray-900 dark:text-gray-100">{{ target.keyword }}</td>
                <td class="px-4 py-2 text-gray-600 dark:text-gray-400">{{ formatVolume(target.search_volume) }}</td>
                <td class="px-4 py-2">
                  <span :class="tierClass(target.tier)" class="text-xs font-medium px-2 py-0.5 rounded-lg">
                    {{ target.tier }}
                  </span>
                </td>
                <td class="px-4 py-2">
                  <span :class="priorityClass(target.priority_label)" class="text-xs font-medium px-2 py-0.5 rounded-lg">
                    {{ target.priority_label || 'Optional' }}
                  </span>
                </td>
                <td v-for="comp in targetCompetitorColumns" :key="comp" class="px-4 py-2 text-gray-600 dark:text-gray-400">
                  {{ target.competitor_counts?.[comp] ?? '---' }}
                </td>
                <td class="px-4 py-2 text-gray-600 dark:text-gray-400">{{ target.raw_avg ?? '---' }}</td>
                <td class="px-4 py-2 text-gray-600 dark:text-gray-400">{{ target.cleaned_avg ?? '---' }}</td>
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{{ target.target }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="flex items-center justify-between">
          <p v-if="selectedKeywords.size === 0" class="text-sm text-red-600 dark:text-red-400">
            Select at least one keyword to continue.
          </p>
          <div v-else></div>
          <button @click="handleLockTargets" :disabled="locking || selectedKeywords.size === 0 || !canLockTargets" class="btn btn-primary disabled:opacity-50">
            {{ locking ? 'Locking...' : `Confirm ${selectedKeywords.size} Targets & Lock` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import consumer from '~/channels/consumer'
import {
  generateHeadingMetaOptions,
  getHeadingMetaLock,
  lockTitleTag,
  lockMetaDescription,
  lockH1,
  lockHeadingStructure,
  verifyHeadingKeywords,
  lockTargets as lockTargetsApi,
  unlockStep,
  regenerateTitleOptions,
  regenerateMetaOptions,
  regenerateH1Options,
  regenerateHeadingStructure
} from '~/javascript/api/heading_meta_lock.js'

const props = defineProps({
  pageId: { type: String, required: true }
})

const emit = defineEmits(['approved'])

let subscription = null
let pollTimer = null

const lock = ref(null)
const readiness = ref(null)
const loading = ref(true)
const generating = ref(false)
const locking = ref(false)
const error = ref(null)
const progressMessage = ref('')
const selectedTitle = ref(null)
const customTitle = ref('')
const selectedMeta = ref(null)
const customMeta = ref('')
const metaOverrideWarnings = ref(false)
const selectedH1 = ref(null)
const customH1 = ref('')
const editingHeadings = ref([])
const selectedKeywords = ref(new Set())
const confirmedKeyword = ref('')

const titleGuidance = ref('')
const metaGuidance = ref('')
const h1Guidance = ref('')
const headingStructureGuidance = ref('')

const regenerating = ref({ title: false, meta: false, h1: false, structure: false })
const regenerateError = ref({ title: null, meta: null, h1: null, structure: null })

const steps = [
  { key: 'title_tag', label: 'Title Tag' },
  { key: 'meta_description', label: 'Meta Desc' },
  { key: 'h1', label: 'H1' },
  { key: 'heading_structure', label: 'Headings' },
  { key: 'keyword_verification', label: 'Verify' },
  { key: 'target_lock', label: 'Targets' }
]

const stepOrder = steps.map((s) => s.key)

const lockedSteps = computed(() => {
  if (!lock.value?.step_statuses) return []
  return stepOrder.filter((s) => lock.value.step_statuses[s] === 'locked')
})

const stepStatus = (key) => lock.value?.step_statuses?.[key] || 'pending'

const stepDotClass = (key) => {
  const status = stepStatus(key)
  if (status === 'locked') return 'bg-primary-500 border-primary-500 text-white'
  if (status === 'active') return 'bg-primary-500 border-primary-500 text-white'
  return 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
}

const stepLabelClass = (key) => {
  const status = stepStatus(key)
  if (status === 'locked') return 'text-primary-600 dark:text-primary-400'
  if (status === 'active') return 'text-primary-600 dark:text-primary-400 font-medium'
  return 'text-gray-400 dark:text-gray-500'
}

const stepLineClass = (key) => {
  const status = stepStatus(key)
  if (status === 'locked') return 'bg-primary-500'
  return 'bg-gray-300 dark:bg-gray-600'
}

const stepLabel = (key) => steps.find((s) => s.key === key)?.label || key

const existingStructure = computed(() => {
  const live = lock.value?.existing_page_structure || {}
  const context = lock.value?.generation_context || {}
  const liveHeadings = Array.isArray(live.headings) ? live.headings : []
  const contextHeadings = Array.isArray(context.current_headings) ? context.current_headings : []

  return {
    title_tag: live.title_tag || context.current_title || '',
    title_source: live.title_source || context.current_title_source || null,
    meta_description: live.meta_description || context.current_meta || '',
    meta_description_source: live.meta_description_source || context.current_meta_source || null,
    h1: live.h1 || context.current_h1 || '',
    h1_source: live.h1_source || context.current_h1_source || null,
    headings: liveHeadings.length ? liveHeadings : contextHeadings,
    last_extract_content_at: live.last_extract_content_at || null
  }
})

const existingTitleTag = computed(() => existingStructure.value.title_tag.toString().trim())
const existingMetaDescription = computed(() => existingStructure.value.meta_description.toString().trim())
const existingH1 = computed(() => existingStructure.value.h1.toString().trim())
const existingHeadings = computed(() => existingStructure.value.headings || [])
const effectiveReadiness = computed(() => lock.value?.readiness || readiness.value || {})
const readinessBlocking = computed(() => effectiveReadiness.value.blocking_messages || [])
const readinessWarnings = computed(() => effectiveReadiness.value.warnings || [])
const canGenerate = computed(() => effectiveReadiness.value.can_run === true && confirmedKeyword.value.trim().length > 0)
const readinessSummary = computed(() => {
  if (effectiveReadiness.value.can_run) return 'Ready: topic plan, counting, keyword, and extracted content are verified.'
  return readinessBlocking.value[0] || 'Structure readiness has not been checked yet.'
})

const lockedValue = (step) => {
  if (!lock.value) return ''
  switch (step) {
    case 'title_tag': return lock.value.locked_title_tag || ''
    case 'meta_description': return lock.value.locked_meta_description || ''
    case 'h1': return lock.value.locked_h1 || ''
    case 'heading_structure': {
      const headings = lock.value.locked_heading_structure || []
      return headings.map((h) => `H${h.level}: ${h.text}`).join(' | ')
    }
    case 'keyword_verification': {
      const v = lock.value.verification_summary
      return v ? `${v.covered}/${v.total} keywords covered` : 'Verified'
    }
    case 'target_lock': return 'Targets locked'
    default: return ''
  }
}

const customH1ContainsKeyword = computed(() => {
  if (!customH1.value || !lock.value?.generation_context?.primary_keyword) return true
  const kw = lock.value.generation_context.primary_keyword.toLowerCase()
  return customH1.value.toLowerCase().includes(kw)
})

const nestingWarning = computed(() => {
  for (let i = 1; i < editingHeadings.value.length; i += 1) {
    const prev = editingHeadings.value[i - 1]
    const curr = editingHeadings.value[i]
    if (curr.level > prev.level + 1) {
      return `Heading "${curr.text || '(empty)'}" skips a level (H${prev.level} to H${curr.level}). Consider fixing the nesting.`
    }
  }
  return null
})

const targetQualitySummary = computed(() => lock.value?.target_quality_summary || {})
const targetBlockingMessages = computed(() => targetQualitySummary.value.blocking_messages || [])
const targetWarnings = computed(() => targetQualitySummary.value.warnings || [])
const canLockTargets = computed(() => targetQualitySummary.value.can_lock !== false)

const allKeywordsSelected = computed(() => {
  const targets = lock.value?.locked_targets || []
  return targets.length > 0 && selectedKeywords.value.size === targets.length
})

const someKeywordsSelected = computed(() => selectedKeywords.value.size > 0)

const toggleKeyword = (keyword) => {
  const next = new Set(selectedKeywords.value)
  if (next.has(keyword)) {
    next.delete(keyword)
  } else {
    next.add(keyword)
  }
  selectedKeywords.value = next
}

const selectAllKeywords = () => {
  const targets = lock.value?.locked_targets || []
  selectedKeywords.value = new Set(targets.map((t) => t.keyword))
}

const deselectAllKeywords = () => {
  selectedKeywords.value = new Set()
}

const toggleAllKeywords = () => {
  if (allKeywordsSelected.value) {
    deselectAllKeywords()
  } else {
    selectAllKeywords()
  }
}

const targetCompetitorColumns = computed(() => {
  const targets = lock.value?.locked_targets || []
  if (!targets.length) return []
  const first = targets[0]
  return first.competitor_counts ? Object.keys(first.competitor_counts) : []
})

const charCountClass = (text, limit) => {
  const len = (text || '').length
  if (len > limit) return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  if (len > limit * 0.9) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
  return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
}

const headingLevelClass = (level) => {
  const map = {
    1: 'bg-primary-200 dark:bg-primary-900/50 text-primary-800 dark:text-primary-200',
    2: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    3: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    4: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
  }
  return map[level] || map[4]
}

const tierClass = (tier) => {
  const map = {
    core: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    high_value: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    primary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    secondary: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    tertiary: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    supporting: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
  }
  return map[tier] || map.supporting
}

const priorityClass = (priority) => {
  const key = (priority || '').toString().toLowerCase()
  if (key === 'critical') return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
  if (key === 'useful') return 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
  if (key === 'remove') return 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
  return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
}

const sourceLabel = (source) => {
  if (!source) return 'Source not checked'
  if (source.found === false) return `${source.source || 'Saved source'} checked; not found`
  const confidence = source.data_confidence ? ` (${source.data_confidence})` : ''
  return `${source.source || 'Saved source'}${confidence}`
}

const sortedTargets = computed(() => {
  const targets = lock.value?.locked_targets || []
  return [...targets].sort((a, b) => (b.search_volume ?? -1) - (a.search_volume ?? -1))
})

const formatVolume = (vol) => {
  if (vol == null) return '---'
  if (vol >= 1000) return `${(vol / 1000).toFixed(1)}k`
  return vol.toLocaleString()
}

const formatSource = (source) => {
  const map = {
    competitor_consensus: 'Consensus',
    topic_gap: 'Topic Gap',
    paa: 'PAA',
    semantic: 'Semantic'
  }
  return map[source] || source
}

const formatDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString()
}

// Initialize heading editor when reaching step 4
watch(() => lock.value?.current_step, (step) => {
  if (step === 'heading_structure' && lock.value?.heading_structure_recommendation) {
    editingHeadings.value = JSON.parse(JSON.stringify(lock.value.heading_structure_recommendation))
  }
  // Initialize only useful targets as selected when reaching target_lock
  if (step === 'target_lock' && lock.value?.locked_targets) {
    selectedKeywords.value = new Set(lock.value.locked_targets.filter((t) => t.default_selected !== false).map((t) => t.keyword))
  }
}, { immediate: true })

const addHeading = () => {
  const lastLevel = editingHeadings.value.length ? editingHeadings.value[editingHeadings.value.length - 1].level : 2
  editingHeadings.value.push({ level: Math.min(lastLevel, 4), text: '', keywords: [], source: null })
}

const removeHeading = (index) => {
  editingHeadings.value.splice(index, 1)
}

const indentHeading = (index) => {
  if (editingHeadings.value[index].level < 4) {
    editingHeadings.value[index].level += 1
  }
}

const outdentHeading = (index) => {
  if (editingHeadings.value[index].level > 2) {
    editingHeadings.value[index].level -= 1
  }
}

// Drag-and-drop reordering. Dragging is armed from the grip handle only, so
// text selection inside the heading inputs keeps working.
const draggingIndex = ref(null)
const dragOverIndex = ref(null)
const dragEnabledIndex = ref(null)

const onDragStart = (index, event) => {
  draggingIndex.value = index
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', String(index))
}

const onDrop = (index) => {
  if (draggingIndex.value !== null && draggingIndex.value !== index) {
    const [moved] = editingHeadings.value.splice(draggingIndex.value, 1)
    editingHeadings.value.splice(index, 0, moved)
  }
  onDragEnd()
}

const onDragEnd = () => {
  draggingIndex.value = null
  dragOverIndex.value = null
  dragEnabledIndex.value = null
}

const moveHeading = (index, delta) => {
  const target = index + delta
  if (target < 0 || target >= editingHeadings.value.length) return

  const [moved] = editingHeadings.value.splice(index, 1)
  editingHeadings.value.splice(target, 0, moved)
}

const updateLock = (data) => {
  lock.value = data.heading_meta_lock
  readiness.value = data.readiness || data.heading_meta_lock?.readiness || readiness.value
  confirmedKeyword.value = data.heading_meta_lock?.generation_context?.primary_keyword || readiness.value?.selected_keyword || confirmedKeyword.value
  error.value = null
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async() => {
    try {
      const response = await getHeadingMetaLock(props.pageId)
      const l = response.data.heading_meta_lock
      readiness.value = response.data.readiness || readiness.value
      confirmedKeyword.value = l?.generation_context?.primary_keyword || readiness.value?.selected_keyword || confirmedKeyword.value
      if (l && l.status !== 'pending' && l.status !== 'generating') {
        lock.value = l
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

const fetchLock = async() => {
  try {
    loading.value = true
    const response = await getHeadingMetaLock(props.pageId)
    lock.value = response.data.heading_meta_lock
    readiness.value = response.data.readiness || lock.value?.readiness || null
    confirmedKeyword.value = lock.value?.generation_context?.primary_keyword || readiness.value?.selected_keyword || ''
    // Auto-populate keyword verification if step 5 has empty data
    if (lock.value?.current_step === 'keyword_verification' &&
        (!lock.value.heading_keyword_verification || lock.value.heading_keyword_verification.length === 0)) {
      try {
        const verifyResponse = await verifyHeadingKeywords(props.pageId)
        updateLock(verifyResponse.data)
      } catch (_e) { /* ignore - user can click Accept manually */ }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching heading meta lock:', e)
  } finally {
    loading.value = false
  }
}

const subscribeToChannel = () => {
  if (subscription) subscription.unsubscribe()
  subscription = consumer.subscriptions.create(
    { channel: 'HeadingMetaLockChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (data.step === 'completed' || data.step === 'failed') {
          stopPolling()
          fetchLock()
        }
      }
    }
  )
}

const handleGenerate = async() => {
  try {
    generating.value = true
    error.value = null
    await generateHeadingMetaOptions(props.pageId, confirmedKeyword.value)
    subscribeToChannel()
    lock.value = { status: 'pending', current_step: 'title_tag', step_statuses: {} }
    loading.value = false
    startPolling()
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to start generation'
    readiness.value = e.response?.data?.readiness || readiness.value
    // eslint-disable-next-line no-console
    console.error('Failed to generate heading meta options:', e)
  } finally {
    generating.value = false
  }
}

const handleLockTitle = async() => {
  try {
    locking.value = true
    error.value = null
    const title = selectedTitle.value !== null ?
      lock.value.title_tag_options[selectedTitle.value].text :
      customTitle.value
    if (!title) { error.value = 'Please select or enter a title tag'; return }
    const response = await lockTitleTag(props.pageId, title)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock title tag'
  } finally {
    locking.value = false
  }
}

const handleLockMeta = async() => {
  try {
    locking.value = true
    error.value = null
    const desc = selectedMeta.value !== null ?
      lock.value.meta_description_options[selectedMeta.value].text :
      customMeta.value
    if (!desc) { error.value = 'Please select or enter a meta description'; return }
    const response = await lockMetaDescription(props.pageId, desc, metaOverrideWarnings.value)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock meta description'
  } finally {
    locking.value = false
  }
}

const handleLockH1 = async() => {
  try {
    locking.value = true
    error.value = null
    const h1Val = selectedH1.value !== null ?
      lock.value.h1_options[selectedH1.value].text :
      customH1.value
    if (!h1Val) { error.value = 'Please select or enter an H1'; return }
    const response = await lockH1(props.pageId, h1Val)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock H1'
  } finally {
    locking.value = false
  }
}

const handleLockHeadingStructure = async() => {
  try {
    locking.value = true
    error.value = null
    const headings = editingHeadings.value.filter((h) => h.text.trim())
    if (!headings.length) { error.value = 'Please add at least one heading'; return }
    const response = await lockHeadingStructure(props.pageId, headings)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock heading structure'
  } finally {
    locking.value = false
  }
}

const handleRegenerateTitle = async() => {
  regenerating.value.title = true
  regenerateError.value.title = null
  try {
    const response = await regenerateTitleOptions(props.pageId, titleGuidance.value)
    updateLock(response.data)
    selectedTitle.value = null
  } catch (e) {
    regenerateError.value.title = e.response?.data?.message || 'Failed to regenerate title options'
  } finally {
    regenerating.value.title = false
  }
}

const handleRegenerateMeta = async() => {
  regenerating.value.meta = true
  regenerateError.value.meta = null
  try {
    const response = await regenerateMetaOptions(props.pageId, metaGuidance.value)
    updateLock(response.data)
    selectedMeta.value = null
  } catch (e) {
    regenerateError.value.meta = e.response?.data?.message || 'Failed to regenerate meta options'
  } finally {
    regenerating.value.meta = false
  }
}

const handleRegenerateH1 = async() => {
  regenerating.value.h1 = true
  regenerateError.value.h1 = null
  try {
    const response = await regenerateH1Options(props.pageId, h1Guidance.value)
    updateLock(response.data)
    selectedH1.value = null
  } catch (e) {
    regenerateError.value.h1 = e.response?.data?.message || 'Failed to regenerate H1 options'
  } finally {
    regenerating.value.h1 = false
  }
}

const handleRegenerateHeadingStructure = async() => {
  regenerating.value.structure = true
  regenerateError.value.structure = null
  try {
    const response = await regenerateHeadingStructure(props.pageId, headingStructureGuidance.value)
    updateLock(response.data)
    editingHeadings.value = (lock.value?.heading_structure_recommendation || []).map((h) => ({ ...h }))
  } catch (e) {
    regenerateError.value.structure = e.response?.data?.message || 'Failed to regenerate heading structure'
  } finally {
    regenerating.value.structure = false
  }
}

const handleAcceptVerification = async() => {
  try {
    locking.value = true
    error.value = null
    const response = await verifyHeadingKeywords(props.pageId)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to verify keywords'
  } finally {
    locking.value = false
  }
}

const handleLockTargets = async() => {
  try {
    locking.value = true
    error.value = null
    const response = await lockTargetsApi(props.pageId, [...selectedKeywords.value])
    updateLock(response.data)
    if (response.data.heading_meta_lock?.approved) {
      emit('approved')
    }
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to lock targets'
  } finally {
    locking.value = false
  }
}

const handleUnlock = async(step) => {
  try {
    locking.value = true
    error.value = null
    const response = await unlockStep(props.pageId, step)
    updateLock(response.data)
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to unlock step'
  } finally {
    locking.value = false
  }
}

onMounted(async() => {
  await fetchLock()
  subscribeToChannel()
  if (lock.value?.status === 'pending' || lock.value?.status === 'generating') {
    startPolling()
  }
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
